import {
  BulkMessage,
  BulkMessageCampaignTypeEnum,
  BulkMessageSendStatusEnum,
  BulkMessageSortableFieldEnum,
  SortDirectionEnum,
  Student,
  User,
} from "@/__generated__/types";
import { DataTable } from "@/components/DataTable";
import { Column, ColumnDef, Row } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { BulkMessageAvatarCell } from "@/pages/BulkMessageOverview/BulkMessageAvatarCell";
import { formatDate } from "@/lib/formatDate";
import { useLocale } from "./useLocale";
import { Locale } from "@/i18n";
import { ActiveBulkMessageActions } from "@/pages/BulkMessageOverview/ActiveBulkMessageActions";
import { EditBulkMessageActions } from "@/pages/BulkMessageOverview/EditBulkMessageActions";
import { SentBulkMessageActions } from "@/pages/BulkMessageOverview/SentBulkMessageActions";

interface ColumnConfig {
  bulkMessageType: BulkMessageCampaignTypeEnum[];
  emptyStateLabel: string;
  title: string;
  description: string;
  ordering: {
    field: BulkMessageSortableFieldEnum;
    direction: SortDirectionEnum;
  }[];
  columnsConfig?: (
    t: (key: string) => string,
    locale?: string,
  ) => Partial<{
    name: ColumnDef<BulkMessage | null>;
    campaignType: ColumnDef<BulkMessage | null>;
    sentFrom: ColumnDef<BulkMessage | null>;
    startSendingAt: ColumnDef<BulkMessage | null>;
    stopSendingAt: ColumnDef<BulkMessage | null>;
    sentTo: ColumnDef<BulkMessage | null>;
    actions: ColumnDef<BulkMessage | null>;
  }>;
}

const DATE_FORMAT = "PPP";
const PLACEHOLDER_FALLBACK = "--";

export const BulkMessageColumnsId: Record<string, string> = {
  name: "name",
  campaignType: "campaignType",
  sentFrom: "sentFrom",
  startSendingAt: "startSendingAt",
  stopSendingAt: "stopSendingAt",
  sentTo: "sentTo",
  actions: "actions",
};

const canSort: (field: string) => boolean = (field: string): boolean => {
  return Object.values(BulkMessageSortableFieldEnum).includes(
    field as BulkMessageSortableFieldEnum,
  );
};

const MAP_SORTING_FIELD_TO_COLUMN_ID: Record<
  string,
  BulkMessageSortableFieldEnum
> = {
  campaignType: BulkMessageSortableFieldEnum.CampaignType,
  name: BulkMessageSortableFieldEnum.Name,
  sentFrom: BulkMessageSortableFieldEnum.SentFrom,
  startSendingAt: BulkMessageSortableFieldEnum.StartSendingAt,
  stopSendingAt: BulkMessageSortableFieldEnum.StopSendingAt,
};

export const MAP_COLUMS_CONFIG_BY_SEND_STATUS: Record<
  BulkMessageSendStatusEnum,
  ColumnConfig
> = {
  [BulkMessageSendStatusEnum.Active]: {
    title: "bulkMessaging.table.active",
    description: "bulkMessaging.table.descriptions.active",
    bulkMessageType: [BulkMessageCampaignTypeEnum.Campaign],
    emptyStateLabel: "bulkMessaging.table.emptyStates.active",
    ordering: [
      {
        field: BulkMessageSortableFieldEnum.StartSendingAt,
        direction: SortDirectionEnum.Desc,
      },
    ],
    columnsConfig: () => ({
      actions: {
        id: "actions",
        header: "",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }: { row: Row<BulkMessage | null> }) => {
          return <ActiveBulkMessageActions row={row} />;
        },
      },
    }),
  },
  [BulkMessageSendStatusEnum.Draft]: {
    title: "bulkMessaging.table.draft",
    description: "bulkMessaging.table.descriptions.draft",
    bulkMessageType: [
      BulkMessageCampaignTypeEnum.Campaign,
      BulkMessageCampaignTypeEnum.OneTimeSend,
    ],
    emptyStateLabel: "bulkMessaging.table.emptyStates.draft",
    ordering: [
      {
        field: BulkMessageSortableFieldEnum.StartSendingAt,
        direction: SortDirectionEnum.Desc,
      },
    ],
    columnsConfig: (t) => ({
      sentTo: {
        id: "sentTo",
        accessorFn: (row: BulkMessage | null) => {
          const { audienceReach } = row || {};
          return audienceReach?.nodes?.[0]?.firstName ?? "";
        },
        enableSorting: true,
        enableColumnFilter: false,
        meta: {
          label: t("bulkMessaging.table.audience"),
        },
        header: ({ column }: { column: Column<BulkMessage | null> }) => (
          <DataTable.THeaderCellSorting column={column}>
            {t("bulkMessaging.table.audience")}
          </DataTable.THeaderCellSorting>
        ),
        cell: ({ row }: { row: Row<BulkMessage | null> }) => {
          const { audienceReach } = row.original || {};
          return (
            <DataTable.TBodyCell>
              <BulkMessageAvatarCell
                users={audienceReach?.nodes as Student[]}
                totalCount={audienceReach?.totalCount}
              />
            </DataTable.TBodyCell>
          );
        },
      },
      actions: {
        id: "actions",
        header: "",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }: { row: Row<BulkMessage | null> }) => {
          return <EditBulkMessageActions row={row} />;
        },
      },
    }),
  },
  [BulkMessageSendStatusEnum.Scheduled]: {
    title: "bulkMessaging.table.scheduled",
    description: "bulkMessaging.table.descriptions.scheduled",
    bulkMessageType: [
      BulkMessageCampaignTypeEnum.Campaign,
      BulkMessageCampaignTypeEnum.OneTimeSend,
    ],
    emptyStateLabel: "bulkMessaging.table.emptyStates.scheduled",
    ordering: [
      {
        field: BulkMessageSortableFieldEnum.StartSendingAt,
        direction: SortDirectionEnum.Desc,
      },
    ],
    columnsConfig: (t, locale?: string) => ({
      stopSendingAt: {
        id: "stopSendingAt",
        accessorFn: (row: BulkMessage | null) => {
          return formatStopSendingAt(row, locale as Locale, "");
        },
        enableSorting: true,
        enableColumnFilter: true,
        meta: {
          label: t("bulkMessaging.table.stopSendingAt"),
        },
        header: ({ column }: { column: Column<BulkMessage | null> }) => (
          <DataTable.THeaderCellSorting column={column}>
            {t("bulkMessaging.table.stopSendingAt")}
          </DataTable.THeaderCellSorting>
        ),
        cell: ({ row }: { row: Row<BulkMessage | null> }) => {
          return (
            <DataTable.TBodyCell>
              {formatStopSendingAt(row.original, locale as Locale)}
            </DataTable.TBodyCell>
          );
        },
      },
      sentTo: {
        id: "sentTo",
        accessorFn: (row: BulkMessage | null) => {
          const { audienceReach } = row || {};
          return audienceReach?.nodes?.[0]?.firstName ?? "";
        },
        enableSorting: true,
        enableColumnFilter: false,
        meta: {
          label: t("bulkMessaging.table.audience"),
        },
        header: ({ column }: { column: Column<BulkMessage | null> }) => (
          <DataTable.THeaderCellSorting column={column}>
            {t("bulkMessaging.table.audience")}
          </DataTable.THeaderCellSorting>
        ),
        cell: ({ row }: { row: Row<BulkMessage | null> }) => {
          const { audienceReach } = row.original || {};
          return (
            <DataTable.TBodyCell>
              <BulkMessageAvatarCell
                users={audienceReach?.nodes as Student[]}
                totalCount={audienceReach?.totalCount}
              />
            </DataTable.TBodyCell>
          );
        },
      },
      actions: {
        id: "actions",
        header: "",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }: { row: Row<BulkMessage | null> }) => {
          return <EditBulkMessageActions row={row} />;
        },
      },
    }),
  },
  [BulkMessageSendStatusEnum.Sent]: {
    title: "bulkMessaging.table.completed",
    description: "bulkMessaging.table.descriptions.completed",
    bulkMessageType: [
      BulkMessageCampaignTypeEnum.Campaign,
      BulkMessageCampaignTypeEnum.OneTimeSend,
    ],
    emptyStateLabel: "bulkMessaging.table.emptyStates.completed",
    ordering: [
      {
        field: BulkMessageSortableFieldEnum.StartSendingAt,
        direction: SortDirectionEnum.Desc,
      },
    ],
    columnsConfig: () => ({
      actions: {
        id: "actions",
        header: "",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }: { row: Row<BulkMessage | null> }) => {
          return <SentBulkMessageActions row={row} />;
        },
      },
    }),
  },
};

export function useBulkMessageTableConfig(
  sendStatus: BulkMessageSendStatusEnum,
) {
  const { t } = useTranslation();
  const columnsConfigBasedOnSendStatus =
    MAP_COLUMS_CONFIG_BY_SEND_STATUS[sendStatus];
  const locale = useLocale();
  const [ordering, setOrdering] = useState<
    [
      {
        field: BulkMessageSortableFieldEnum;
        direction: SortDirectionEnum;
      },
    ]
  >(
    columnsConfigBasedOnSendStatus.ordering as [
      {
        field: BulkMessageSortableFieldEnum;
        direction: SortDirectionEnum;
      },
    ],
  );

  const campaignTypeMap = {
    [BulkMessageCampaignTypeEnum.Campaign]: t("bulkMessaging.table.campaign"),
    [BulkMessageCampaignTypeEnum.OneTimeSend]: t(
      "bulkMessaging.table.oneTimeSend",
    ),
  };

  const handleSortingChange = ({
    orderingField,
    orderingDirection,
  }: {
    orderingField: string;
    orderingDirection: SortDirectionEnum;
  }) => {
    setOrdering([
      {
        field: MAP_SORTING_FIELD_TO_COLUMN_ID[orderingField],
        direction: orderingDirection,
      },
    ]);
  };

  const overviewMessagesColumnsConfig: ColumnDef<BulkMessage | null>[] = [
    {
      id: BulkMessageColumnsId.name,
      accessorFn: (row: BulkMessage | null) => row?.name ?? "",
      enableSorting: canSort(BulkMessageColumnsId.name),
      enableColumnFilter: true,

      header: ({ column }: { column: Column<BulkMessage | null> }) => (
        <DataTable.THeaderCellSorting
          column={column}
          onSortingChange={handleSortingChange}
        >
          {t("bulkMessaging.table.name")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<BulkMessage | null> }) => {
        return <DataTable.TBodyCell>{row.original?.name}</DataTable.TBodyCell>;
      },
      ...(columnsConfigBasedOnSendStatus.columnsConfig?.(t)?.name || {}),
    },
    {
      id: BulkMessageColumnsId.campaignType,
      accessorFn: (row: BulkMessage | null) =>
        campaignTypeMap[row?.campaignType as BulkMessageCampaignTypeEnum],
      enableSorting: canSort(BulkMessageColumnsId.campaignType),
      enableColumnFilter: true,
      meta: {
        label: t("bulkMessaging.table.campaignType"),
      },
      header: ({ column }: { column: Column<BulkMessage | null> }) => (
        <DataTable.THeaderCellSorting
          column={column}
          onSortingChange={handleSortingChange}
        >
          {t("bulkMessaging.table.campaignType")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<BulkMessage | null> }) => {
        return (
          <DataTable.TBodyCell>
            {
              campaignTypeMap[
                row.original?.campaignType as BulkMessageCampaignTypeEnum
              ]
            }
          </DataTable.TBodyCell>
        );
      },
      ...(columnsConfigBasedOnSendStatus.columnsConfig?.(t)?.campaignType ||
        {}),
    },
    {
      id: BulkMessageColumnsId.sentFrom,
      accessorFn: (row: BulkMessage | null) => row?.sentFrom?.firstName ?? "",
      enableSorting: canSort(BulkMessageColumnsId.sentFrom),
      enableColumnFilter: true,
      filterFn: (row, id, value) => {
        const sentFrom = row.getValue(id) as string;
        return sentFrom.toLowerCase().includes(value.toLowerCase());
      },
      meta: {
        label: t("bulkMessaging.table.sentFrom"),
        avatarFilterLabel: true,
      },
      header: ({ column }: { column: Column<BulkMessage | null> }) => (
        <DataTable.THeaderCellSorting
          column={column}
          onSortingChange={handleSortingChange}
        >
          {t("bulkMessaging.table.sentFrom")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<BulkMessage | null> }) => {
        return (
          <BulkMessageAvatarCell
            users={[row.original?.sentFrom] as User[]}
            senderType={row.original?.senderType}
          />
        );
      },
      ...(columnsConfigBasedOnSendStatus.columnsConfig?.(t)?.sentFrom || {}),
    },
    {
      id: BulkMessageColumnsId.startSendingAt,
      accessorFn: (row: BulkMessage | null) => {
        const { startSendingAt } = row || {};
        return startSendingAt
          ? formatDate(startSendingAt, DATE_FORMAT, {
              locale,
            })
          : "";
      },
      enableSorting: canSort(BulkMessageColumnsId.startSendingAt),
      enableColumnFilter: true,
      meta: {
        label: t("bulkMessaging.table.startSendingAt"),
      },
      header: ({ column }: { column: Column<BulkMessage | null> }) => (
        <DataTable.THeaderCellSorting
          column={column}
          onSortingChange={handleSortingChange}
        >
          {t("bulkMessaging.table.startSendingAt")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<BulkMessage | null> }) => {
        const { startSendingAt } = row.original || {};
        return (
          <DataTable.TBodyCell>
            {startSendingAt
              ? formatDate(startSendingAt, DATE_FORMAT, {
                  locale,
                })
              : PLACEHOLDER_FALLBACK}
          </DataTable.TBodyCell>
        );
      },
      ...(columnsConfigBasedOnSendStatus.columnsConfig?.(t, locale)
        ?.startSendingAt || {}),
    },
    {
      id: BulkMessageColumnsId.stopSendingAt,
      accessorFn: (row: BulkMessage | null) => {
        return formatStopSendingAt(row, locale, "");
      },
      enableSorting: canSort(BulkMessageColumnsId.stopSendingAt),
      enableColumnFilter: true,
      meta: {
        label: t("bulkMessaging.table.stopSendingAt"),
      },
      header: ({ column }: { column: Column<BulkMessage | null> }) => (
        <DataTable.THeaderCellSorting
          column={column}
          onSortingChange={handleSortingChange}
        >
          {t("bulkMessaging.table.stopSendingAt")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<BulkMessage | null> }) => {
        return (
          <DataTable.TBodyCell>
            {formatStopSendingAt(row.original, locale)}
          </DataTable.TBodyCell>
        );
      },
      ...(columnsConfigBasedOnSendStatus.columnsConfig?.(t, locale)
        ?.stopSendingAt || {}),
    },
    {
      id: BulkMessageColumnsId.sentTo,
      enableSorting: false,
      enableColumnFilter: false,
      meta: {
        label: t("bulkMessaging.table.sentTo"),
      },
      header: () => (
        <DataTable.THeaderCell>
          {t("bulkMessaging.table.sentTo")}
        </DataTable.THeaderCell>
      ),
      cell: ({ row }: { row: Row<BulkMessage | null> }) => {
        const { sentTo } = row.original || {};
        return (
          <DataTable.TBodyCell>
            <BulkMessageAvatarCell
              users={sentTo?.nodes as Student[]}
              totalCount={sentTo?.totalCount}
            />
          </DataTable.TBodyCell>
        );
      },
      ...(columnsConfigBasedOnSendStatus.columnsConfig?.(t)?.sentTo || {}),
    },
    {
      id: BulkMessageColumnsId.actions,
      header: "",
      enableSorting: false,
      enableColumnFilter: false,
      ...(columnsConfigBasedOnSendStatus.columnsConfig?.(t)?.actions || {}),
    },
  ];

  return {
    columns: overviewMessagesColumnsConfig,
    ordering,
    columnsConfigBasedOnSendStatus,
  };
}

function formatStopSendingAt(
  bulkMessage: BulkMessage | null,
  locale: Locale,
  placeholder = PLACEHOLDER_FALLBACK,
) {
  const { stopSendingAt, campaignType } = bulkMessage || {};
  return stopSendingAt &&
    campaignType !== BulkMessageCampaignTypeEnum.OneTimeSend
    ? formatDate(stopSendingAt, DATE_FORMAT, {
        locale,
      })
    : placeholder;
}
